import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { ArrowRight } from "lucide-react";

const USES = ["Personal", "Business", "Family", "Other"];

export default function RequestAccess() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", intended_use: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Request Early Access | Hussar Treasury";
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", window.location.origin + "/request-access");
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.intended_use) {
      setError("Please select an intended use.");
      return;
    }
    if (!consent) {
      setError("Explicit consent is required to submit a request.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/access-requests", { ...form, consent });
      setDone(true);
      toast.success("Request recorded.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="request-access-page" className="pt-40 px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="metadata-label mb-6">Limited Cohorts</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-hussar-ink tracking-tight leading-[1.05]">
              Request Early Access
            </h1>
            <p className="mt-8 text-hussar-slate font-light leading-relaxed">
              Hussar Treasury is not currently open to the public. Access is reviewed personally and granted in
              limited cohorts to individuals, families, and businesses whose capital deserves discipline.
            </p>
            <div className="mt-10 border-l-2 border-hussar-gold/50 pl-6">
              <p className="font-serif italic text-hussar-ink/80 text-lg leading-relaxed">
                "Every dollar has a job. Tell us what yours should be doing."
              </p>
            </div>
            <div className="mt-12 space-y-3 font-mono text-[11px] uppercase tracking-[0.2em] text-hussar-muted">
              <div>— Reviewed personally, never by algorithm</div>
              <div>— No public signup exists</div>
              <div>— Your information is never shared or sold</div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15}>
            {done ? (
              <div data-testid="request-access-success" className="border border-hussar-gold/30 bg-hussar-card p-10 sm:p-14">
                <div className="metadata-label mb-4">Request Recorded</div>
                <h2 className="font-serif text-2xl sm:text-3xl text-hussar-ink leading-snug mb-6">
                  Your request is on the record.
                </h2>
                <p className="text-hussar-slate font-light leading-relaxed">
                  A confirmation has been sent to your email. Requests are reviewed in cohorts — there is nothing
                  further you need to do. We will write to you if a place opens.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="request-access-form" className="border border-white/5 bg-hussar-card p-8 sm:p-12 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="metadata-label !text-[10px] block mb-2">Full Name *</label>
                    <input required value={form.name} onChange={set("name")} data-testid="ra-name-input" className="field-input" placeholder="Your name" />
                  </div>
                  <div>
                    <label className="metadata-label !text-[10px] block mb-2">Email *</label>
                    <input required type="email" value={form.email} onChange={set("email")} data-testid="ra-email-input" className="field-input" placeholder="your@address.com" />
                  </div>
                  <div>
                    <label className="metadata-label !text-[10px] block mb-2">Organization / Company</label>
                    <input value={form.organization} onChange={set("organization")} data-testid="ra-org-input" className="field-input" placeholder="Optional" />
                  </div>
                  <div>
                    <label className="metadata-label !text-[10px] block mb-2">Role</label>
                    <input value={form.role} onChange={set("role")} data-testid="ra-role-input" className="field-input" placeholder="Optional" />
                  </div>
                </div>

                <div>
                  <label className="metadata-label !text-[10px] block mb-3">Intended Use *</label>
                  <div className="flex flex-wrap gap-3" data-testid="ra-intended-use-group">
                    {USES.map((u) => (
                      <button
                        type="button"
                        key={u}
                        onClick={() => setForm({ ...form, intended_use: u })}
                        data-testid={`ra-use-${u.toLowerCase()}`}
                        className={`font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors duration-300 ${
                          form.intended_use === u
                            ? "border-hussar-gold text-hussar-gold bg-hussar-gold/5"
                            : "border-white/10 text-hussar-muted hover:border-hussar-gold/40 hover:text-hussar-slate"
                        }`}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="metadata-label !text-[10px] block mb-2">Message</label>
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    rows={4}
                    data-testid="ra-message-input"
                    className="field-input resize-none"
                    placeholder="Optional — what should your capital be doing?"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="ra-consent-checkbox"
                    className="mt-1 accent-[#C5A059]"
                  />
                  <span className="text-xs text-hussar-muted leading-relaxed group-hover:text-hussar-slate transition-colors">
                    I explicitly consent to Hussar Treasury storing this information and contacting me about access.
                    My information is never shared, sold, or shown publicly.
                  </span>
                </label>

                {error && <p data-testid="ra-error" className="text-red-400 text-xs font-mono">{error}</p>}

                <button type="submit" disabled={loading} data-testid="request-access-submit-btn" className="btn-gold w-full disabled:opacity-50">
                  {loading ? "Recording…" : "Request Early Access"} <ArrowRight size={14} />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </div>
  );
}
